import React, { useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useRevealSections } from '../hooks/useReveal';
import { estimateReadMinutes, getI3BlogPostBySlug, getI3BlogPostsSorted } from '../data/i3BlogPosts';
import { useI3SiteContent } from '../cms/useI3SiteContent';
import {
  cmsPostToLegacy,
  findCmsBlogPost,
  isBlogPostArchived,
  isBlogPostPubliclyVisible,
  mergeBlogPostsForIndex,
} from '../cms/blogVisibility';
import { BOOK_SESSION_PATH } from '../bookingConfig';
import { getBlogPostImage, getBlogPostImageAlt } from '../utils/blogImages';
import { lnAssets } from '../utils/lnAssets';
import { SITE_PRACTITIONER_CREDENTIALS } from '../sitePlaceholders';
import '../styles/I3Page.css';
import '../styles/I3Reportage.css';
import '../styles/I3Blog.css';

function formatPostDate(iso: string): string {
  try {
    const d = new Date(`${iso}T12:00:00`);
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
}

const ArticleHeroPhoto: React.FC<{ src: string; alt?: string; className?: string }> = ({
  src,
  alt = '',
  className = '',
}) => {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return <div className={`i3-blog-mockup-photo-fallback ${className}`.trim()} aria-hidden />;
  }
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      loading="eager"
      decoding="async"
      onError={() => setFailed(true)}
    />
  );
};

const I3BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { content } = useI3SiteContent();
  const wrapRef = useRef<HTMLDivElement>(null);
  useRevealSections(wrapRef);

  const allPosts = useMemo(
    () => mergeBlogPostsForIndex(content.blogPosts, getI3BlogPostsSorted()),
    [content.blogPosts]
  );

  const { post, archived } = useMemo(() => {
    const cms = findCmsBlogPost(content.blogPosts, slug);
    if (cms) {
      if (isBlogPostArchived(cms)) {
        return { post: cmsPostToLegacy(cms), archived: true as const };
      }
      if (!isBlogPostPubliclyVisible(cms)) {
        return { post: undefined, archived: false as const };
      }
      return { post: cmsPostToLegacy(cms), archived: false as const };
    }
    return { post: getI3BlogPostBySlug(slug), archived: false as const };
  }, [content.blogPosts, slug]);

  const relatedPosts = useMemo(() => {
    if (!post) return [];
    const topic = post.topic?.trim();
    const sameTopic = allPosts.filter(
      (p) => p.slug !== post.slug && topic && p.topic?.trim() === topic
    );
    const rest = allPosts.filter((p) => p.slug !== post.slug && !sameTopic.includes(p));
    return [...sameTopic, ...rest].slice(0, 3);
  }, [allPosts, post]);

  const heroImage = post ? getBlogPostImage(post.slug, post.topic, post.heroImage) : '';
  const heroImageAlt = post ? getBlogPostImageAlt(post.slug, post.topic) : '';

  const bodyBlocks = useMemo(() => {
    if (!post) return [];
    const paragraphs = post.paragraphs;
    const quote = post.quote?.trim();
    if (!quote || paragraphs.length < 3) {
      return paragraphs.map((p) => ({ type: 'p' as const, text: p }));
    }
    const splitAt = Math.min(Math.max(2, Math.floor(paragraphs.length / 2)), paragraphs.length - 1);
    const blocks: Array<{ type: 'p' | 'quote'; text: string }> = [];
    paragraphs.forEach((p, i) => {
      blocks.push({ type: 'p', text: p });
      if (i === splitAt - 1) blocks.push({ type: 'quote', text: quote });
    });
    return blocks;
  }, [post]);

  if (!post) {
    return (
      <article className="i3-page i3-reportage-page i3-blog-page i3-blog-page--mockup">
        <div className="i3-page-wrap i3-blog-not-found">
          <h1>Article not found</h1>
          <p>This article may have moved or is not published yet.</p>
          <Link to="/blog" className="i3-cta i3-cta-primary">
            Back to journal
          </Link>
        </div>
      </article>
    );
  }

  const topicLabel = post.topic?.trim() || 'Journal';

  return (
    <article className="i3-page i3-reportage-page i3-blog-page i3-blog-page--mockup i3-blog-page--article">
      <section className="i3-blog-article-hero" aria-labelledby="i3-blog-article-title">
        <div className="i3-blog-article-hero-backdrop" aria-hidden>
          <ArticleHeroPhoto src={heroImage} alt={heroImageAlt} className="i3-blog-article-hero-img" />
          <div className="i3-blog-article-hero-scrim" />
        </div>
        <div className="i3-blog-article-hero-inner">
          <p className="i3-blog-article-hero-topic">{topicLabel.toUpperCase()}</p>
          <h1 id="i3-blog-article-title" className="i3-blog-article-hero-title">
            {post.title}
          </h1>
          <p className="i3-blog-article-hero-meta">
            <span>{formatPostDate(post.publishedAt)}</span>
            <span aria-hidden> · </span>
            <span>{estimateReadMinutes(post)} min read</span>
            <span aria-hidden> · </span>
            <span>{SITE_PRACTITIONER_CREDENTIALS}</span>
          </p>
        </div>
      </section>

      <div className="i3-blog-article-layout" ref={wrapRef}>
        {archived ? (
          <p className="i3-blog-archived-notice i3-reveal" role="status">
            This article has been archived. It is no longer shown on the journal index.
          </p>
        ) : null}

        <p className="i3-blog-editorial-back i3-reveal">
          <Link to="/blog" className="i3-blog-back-link">
            ← Journal
          </Link>
        </p>

        <div className="i3-blog-article-columns">
          <div className="i3-blog-article-main i3-reveal">
            {post.meaning?.trim() && !post.quote?.trim() ? (
              <p className="i3-blog-article-lead">{post.meaning.trim()}</p>
            ) : null}

            <div className="i3-blog-article-body">
              {bodyBlocks.map((block, i) =>
                block.type === 'quote' ? (
                  <blockquote key={`q-${i}`} className="i3-blog-article-pullquote">
                    <span className="i3-blog-article-pullquote-mark" aria-hidden>
                      “
                    </span>
                    <p>{block.text}</p>
                  </blockquote>
                ) : (
                  <p key={`p-${i}`} className="i3-blog-article-p">
                    {block.text}
                  </p>
                )
              )}
            </div>

            {post.meaning?.trim() && post.quote?.trim() ? (
              <p className="i3-blog-article-meaning">
                <strong>Meaning:</strong> {post.meaning.trim()}
              </p>
            ) : null}

            <div className="i3-blog-article-inline-cta">
              <p className="i3-blog-article-inline-cta-text">
                Ready to talk about what you are walking through?
              </p>
              <Link to={BOOK_SESSION_PATH} className="i3-cta i3-cta-primary">
                Book a Session
              </Link>
            </div>
          </div>

          <aside className="i3-blog-article-sidebar i3-reveal" aria-label="Related articles">
            {relatedPosts.length > 0 ? (
              <div className="i3-blog-article-sidebar-card">
                <h2 className="i3-blog-article-sidebar-title">Related Articles</h2>
                <ul className="i3-blog-article-related-list">
                  {relatedPosts.map((related) => (
                    <li key={related.slug}>
                      <Link to={`/blog/${related.slug}`} className="i3-blog-article-related-link">
                        <span className="i3-blog-article-related-title">{related.title}</span>
                        <span className="i3-blog-article-related-meta">
                          {estimateReadMinutes(related)} min read
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <div className="i3-blog-article-sidebar-card i3-blog-article-sidebar-card--cta">
              <h2 className="i3-blog-article-sidebar-title">Need Support?</h2>
              <p className="i3-blog-article-sidebar-copy">
                Counseling is a safe place to process what you have read—and what you are living.
              </p>
              <Link to={BOOK_SESSION_PATH} className="i3-cta i3-cta-primary i3-blog-article-sidebar-cta">
                Book a Consultation
              </Link>
            </div>
          </aside>
        </div>
      </div>

      <section className="i3-blog-mockup-final-cta" aria-labelledby="i3-blog-article-final-cta">
        <div className="i3-blog-mockup-final-cta-backdrop" aria-hidden>
          <ArticleHeroPhoto
            src={lnAssets.photos.bridgeScenic}
            className="i3-blog-mockup-final-cta-img"
          />
          <div className="i3-blog-mockup-final-cta-scrim" />
        </div>
        <div className="i3-blog-mockup-final-cta-inner">
          <h2 id="i3-blog-article-final-cta" className="i3-blog-mockup-final-cta-title">
            Ready to Take the First Step?
          </h2>
          <p className="i3-blog-mockup-final-cta-lead">Healing begins with a conversation.</p>
          <Link to={BOOK_SESSION_PATH} className="i3-cta i3-cta-primary i3-cta-on-dark">
            Schedule a Consultation
          </Link>
        </div>
      </section>
    </article>
  );
};

export default I3BlogPost;
